import React from 'react';
import { TrackingEvent } from '@classes/TrackingEvent';
import { submitQueriesStream } from '@services/genaiQueriesV2';
import { addAdvancedSettingValues, useAdvancedSettings } from '../application/useAdvancedSettings';
import { logFormSubmit, logger } from '@utils/usefulFuncs';
import { runStream } from './useStreamingResult';
import { chatHistoryActions } from '@constants/actionTypes';
import { BASEURL } from '@services/network';
import { useDispatch, useSelector } from 'react-redux';

const initialState = [];

const initRunStream = async (resultId, agentName, dispatch) => {
  if (!resultId) return;
  const includeAgentPlan = Boolean(agentName);
  const params = [`run_id=${encodeURIComponent(resultId)}`];
  if (includeAgentPlan) params.push('include_agent_plan=true');
  const URL = `${BASEURL}/api/v2/genai/queries/stream_events?${params.join('&')}`;
  let prevMessage = null;
  const handlers = {
    onMessage: (data) => {
      const result = JSON.parse(data);
      logger('useChatAskDigitalAnalyst onMessage', 'received', {
        runId: resultId,
        questionIndex: result?.question_index,
        finalAnswer: result?.final_answer === true,
        answerLength: result?.answer?.length || 0,
      });
      if (result.answer !== prevMessage) {
        dispatch({ type: chatHistoryActions.updateResultAnswer, payload: { resultId, answer: result.answer } });
        prevMessage = result.answer;
      }
    },
    onError: () => {
      dispatch({
        type: chatHistoryActions.updateResultError,
        payload: { resultId, error: 'Something went wrong, try again.' },
      });
    },
    onComplete: (data) => {
      const response = JSON.parse(data.data).response;
      const answers = response?.answers || [];
      const row = answers.find((r) => r?.final_answer) || answers[0];
      if (!row) return;
      const payload = { resultId, answer: row.answer };
      if (row.kind === 'agent' && row.agent_executed_plan) {
        payload.executedPlan = row.agent_executed_plan;
      }
      dispatch({ type: chatHistoryActions.updateResultAnswer, payload });
    },
  };
  runStream(URL, handlers);
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_LAST_RESULT_ERROR': {
      const { error } = action.payload;
      return [...state.slice(0, -1), { ...state[state.length - 1], error }];
    }
    case 'ADD_RESULT_ID': {
      if (state.length === 0) return state;
      const updatedState = state.slice(0, -1);
      const lastItem = { ...state[state.length - 1], resultId: action.payload.resultId };
      return [...updatedState, lastItem];
    }
    case 'ADD_QUESTION': {
      return [...state, { type: 'question', question: action.payload.question }, { type: 'result', resultId: null }];
    }
    case 'CLEAR_CHAT_HISTORY_DATA': {
      return [];
    }
    default: {
      return state;
    }
  }
};

const buildExtraContextFromChatHistory = (chatHistory, answerLookup) => {
  // Build a context string that emulates a chat transcript for better LLM comprehension.
  // Format:
  // User: <question>
  // Assistant: <answer>
  // ...
  let context = '';
  let lastSpeaker = null;
  for (const item of chatHistory) {
    if (item.type === 'question') {
      context += (context ? '\n' : '') + `User: ${item.question}`;
      lastSpeaker = 'User';
    } else if (item.type === 'result' && item.resultId) {
      const answer = answerLookup[item.resultId]?.answer;
      if (answer !== undefined && answer !== null && answer !== '') {
        context += `\nAssistant: ${answer}`;
        lastSpeaker = 'Assistant';
      }
    }
  }
  return context;
};

export const useChatAskDigitalAnalyst = (tool, agentName) => {
  const appDispatch = useDispatch();
  const answerLookup = useSelector((state) => state.chatHistory.answerLookup);

  const [state, dispatch] = React.useReducer(reducer, initialState);

  const advancedSettingsValues = useAdvancedSettings();
  const { model } = advancedSettingsValues;

  const handleStreamAnswer = (resultId, agentName) => {
    initRunStream(resultId, agentName, appDispatch);
  };

  const onSubmit = async ({ question, selectedDocs, ticker, model: forceModel }) => {
    const _question = question;
    const _model = forceModel || model;
    const docIds = selectedDocs?.map((doc) => doc.id);
    const extraContext = buildExtraContextFromChatHistory(state, answerLookup);

    TrackingEvent.create('Asked Digital Analyst Question', {
      Question: _question,
      Model: _model,
      Tickers: ticker,
      'Selected Docs': selectedDocs?.map((d) => d.filename),
    });
    dispatch({ type: 'ADD_QUESTION', payload: { question } });

    const settings = { model: _model, user_mode: tool, extra_context: extraContext };
    addAdvancedSettingValues(advancedSettingsValues, settings, ['model']);
    logFormSubmit({ ...settings, question: _question, agent_name: agentName, doc_ids: docIds });
    const result = await submitQueriesStream({
      questions: _question,
      agentName,
      docIds,
      settings,
    });
    if (result.status === 200) {
      const runId = result.runId;
      dispatch({ type: 'ADD_RESULT_ID', payload: { resultId: runId } });
      handleStreamAnswer(runId, agentName);
      return;
    }

    const message = result.data?.error || result.error || 'Something went wrong, try again.';
    TrackingEvent.create('Asked Digital Analyst Question Failed', {
      Question: _question,
      Error: message,
    });
    dispatch({ type: 'UPDATE_LAST_RESULT_ERROR', payload: { error: message } });
    return { error: message };
  };

  const onClearChatHistory = () => {
    dispatch({ type: 'CLEAR_CHAT_HISTORY_DATA' });
    appDispatch({ type: chatHistoryActions.clearChatHistoryData });
  };

  return {
    onSubmit,
    onClearChatHistory,

    chatHistoryData: state,
  };
};
